<script lang="ts">
	import Section from "#lib/web-ui/primitives/section.svelte";
	import CheckCircleIcon from "@lucide/svelte/icons/check-circle";
	import {
		getTemplateContext,
		cloudinaryUrl,
		cloudinarySrcset
	} from "../template-context.svelte";

	let { city, state }: { city: string; state: string } = $props();

	const templateContext = getTemplateContext();

	const benefits = $derived([
		`Leverage local ${city} real estate experts who understand the market`,
		"Receive an almost instant cash home buyer offer that's better than any other company",
		"Forget having strangers moving through your home at inconvenient times",
		"Forget about expensive repairs, unrealistic buyers, or bothersome inspections",
		"Skip fixing up repairs or dealing with predatory contractors",
		"Worry no more about last minute back outs by inexperienced buyers",
		"Choose a closing date that fits your timeline"
	]);
</script>

<Section>
	<div class="flex flex-col gap-8 md:flex-row md:gap-12 lg:gap-16">
		<!-- Left image column -->
		<div class="flex flex-1 items-start">
			<div class="h-full w-full overflow-hidden rounded-lg">
				<img
					src={cloudinaryUrl(templateContext.images.needToSell, { width: 768 })}
					srcset={cloudinarySrcset(templateContext.images.needToSell, 640)}
					width="640"
					height="427"
					sizes="(max-width: 768px) 100vw, 50vw"
					class="h-full w-full object-cover"
					alt="We buy houses in {city}, {state}"
					loading="eager"
					fetchpriority="high"
				/>
			</div>
		</div>

		<!-- Right column with content -->
		<div class="flex-1">
			<h2 class="text-primary text-lg font-bold">Local Home Buyers</h2>
			<p
				class="text-foreground mt-2 text-4xl font-semibold tracking-tight text-pretty sm:text-5xl"
			>
				We Buy Houses in {city}, {state} in Any Condition As-Is and Fast for Cash!
			</p>
			<p class="text-foreground/70 mt-6 text-lg">
				Our streamlined home-selling solution could be the perfect solution for you. Over
				the years, {templateContext.siteName} has purchased thousands of properties from {state}
				property owners eager to sell their homes fast without any hassle or stress. We buy houses
				and homes in any condition, no matter the circumstances leading to the sale.
			</p>

			<div class="mt-8">
				<p class="text-foreground font-semibold">Some of the ways we help you:</p>
				<ul class="mt-4 space-y-3">
					{#each benefits as benefit, index (index)}
						<li class="flex gap-3">
							<div class="flex-shrink-0 pt-0.5">
								<CheckCircleIcon class="text-primary h-5 w-5" />
							</div>
							<span class="text-foreground/70 text-base/7">
								{benefit}
							</span>
						</li>
					{/each}
				</ul>
			</div>

			<div class="text-foreground/70 mt-8 space-y-4 text-base/7">
				<p>
					{templateContext.siteName} is one of the top 'we buy houses in {city}'
					companies. We offer the best home buying service in {state}, handling all the
					necessary repairs before reselling the properties over time. While you could
					attempt this independently, it might prove more expensive, time-consuming, and
					stressful due to a lack of expertise and experience. Whether we profit or not,
					our sellers never have to deal with our challenges.
				</p>

				<p>
					We are experts in providing quick cash offers and are genuinely interested in
					buying your land, rental property, home, or commercial building immediately. If
					you need to
					<strong class="text-foreground font-semibold">sell my house fast {city}</strong>
					without any complications, reach out to {templateContext.siteName} for a cash offer
					within 24 hours.
				</p>
			</div>
		</div>
	</div>
</Section>
